import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { createVehicle } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Loader2, Truck } from 'lucide-react';
import { toast } from 'sonner';

const NewVehiclePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    vehicle_type: 'truck',
    capacity_kg: '',
    capacity_m3: '',
    license_plate: '',
    is_available: true,
    current_lat: '',
    current_lng: '',
    certifications: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, is_available: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const vehicleData = {
        ...formData,
        capacity_kg: parseFloat(formData.capacity_kg),
        capacity_m3: parseFloat(formData.capacity_m3),
        current_lat: formData.current_lat ? parseFloat(formData.current_lat) : null,
        current_lng: formData.current_lng ? parseFloat(formData.current_lng) : null,
        certifications: formData.certifications 
          ? formData.certifications.split(',').map(c => c.trim()).filter(Boolean)
          : []
      };

      await createVehicle(vehicleData);
      toast.success('Véhicule ajouté avec succès !');
      navigate('/fleet');
    } catch (error) {
      console.error('Failed to create vehicle:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="new-vehicle-page">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-orange-600" />
              </div>
              <CardTitle className="text-2xl font-['Chivo']">Nouveau Véhicule</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du véhicule *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ex: Camion 01"
                    required
                    data-testid="vehicle-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license_plate">Immatriculation *</Label>
                  <Input
                    id="license_plate"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleChange}
                    placeholder="AB-123-CD"
                    required
                    data-testid="vehicle-plate-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicle_type">Type de véhicule *</Label>
                <Select 
                  value={formData.vehicle_type} 
                  onValueChange={(v) => handleSelectChange('vehicle_type', v)}
                >
                  <SelectTrigger data-testid="vehicle-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truck">Camion</SelectItem>
                    <SelectItem value="van">Fourgon</SelectItem>
                    <SelectItem value="refrigerated">Frigorifique</SelectItem>
                    <SelectItem value="flatbed">Plateau</SelectItem>
                    <SelectItem value="tanker">Citerne</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Capacity */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacity_kg">Capacité (kg) *</Label>
                  <Input
                    id="capacity_kg"
                    name="capacity_kg"
                    type="number"
                    value={formData.capacity_kg}
                    onChange={handleChange}
                    placeholder="5000"
                    required
                    min="0"
                    data-testid="vehicle-capacity-kg-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capacity_m3">Capacité (m³) *</Label>
                  <Input
                    id="capacity_m3"
                    name="capacity_m3"
                    type="number"
                    value={formData.capacity_m3}
                    onChange={handleChange}
                    placeholder="20"
                    required
                    min="0"
                    step="0.1"
                    data-testid="vehicle-capacity-m3-input"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="current_lat">Latitude actuelle</Label>
                  <Input
                    id="current_lat"
                    name="current_lat"
                    type="number"
                    value={formData.current_lat}
                    onChange={handleChange}
                    placeholder="48.8566"
                    step="0.0001"
                    data-testid="vehicle-lat-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="current_lng">Longitude actuelle</Label>
                  <Input
                    id="current_lng"
                    name="current_lng"
                    type="number"
                    value={formData.current_lng}
                    onChange={handleChange}
                    placeholder="2.3522"
                    step="0.0001"
                    data-testid="vehicle-lng-input"
                  />
                </div>
              </div>

              {/* Certifications */}
              <div className="space-y-2">
                <Label htmlFor="certifications">Certifications (séparées par des virgules)</Label>
                <Input
                  id="certifications"
                  name="certifications"
                  value={formData.certifications}
                  onChange={handleChange}
                  placeholder="ADR, ATP, ISO"
                  data-testid="vehicle-certifications-input"
                />
              </div>

              {/* Availability */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label htmlFor="is_available" className="font-medium">Disponibilité</Label>
                  <p className="text-sm text-slate-500">Le véhicule est-il disponible pour des missions ?</p>
                </div>
                <Switch
                  id="is_available"
                  checked={formData.is_available}
                  onCheckedChange={handleSwitchChange}
                  data-testid="vehicle-available-switch"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  disabled={loading}
                  data-testid="submit-vehicle-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Ajout...
                    </>
                  ) : (
                    'Ajouter le véhicule'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NewVehiclePage;
